/**
 * Routing tab edit model. PURE — no React, no I/O.
 *
 * A ROLE# row is the dial: pointing `fast-text` at a cheaper model is how
 * "cheap model for simple work" is operated, with no deploy. burnRate and
 * minCreditsPerCall live on the ROLE and not on the model, so a vendor swap
 * changes OUR cost and never the tenant's charge (spec A2).
 */

import {
  isModelPriced, modelsForProvider,
  type AiModelDto, type AiProviderDto, type AiRoleDto,
} from '../../../services/aiControlApi';

export interface RoleDraft {
  roleId: string;
  label: string;
  enabled: boolean;
  providerId: string;
  modelId: string;
  fallbackProviderId: string;
  fallbackModelId: string;
  burnRate: number;
  minCreditsPerCall: number;
  reserveCap: number;
}

const DEFAULT_RESERVE_CAP = 50;

export function buildRoleDraft(role: AiRoleDto | undefined, roleId: string): RoleDraft {
  return {
    roleId,
    label: role?.label ?? '',
    enabled: role?.enabled ?? false,
    providerId: role?.providerId ?? '',
    modelId: role?.modelId ?? '',
    // Empty string, never undefined — the dropdowns are controlled and an
    // undefined value would make one uncontrolled mid-edit.
    fallbackProviderId: role?.fallbackProviderId ?? '',
    fallbackModelId: role?.fallbackModelId ?? '',
    burnRate: role?.burnRate ?? 1,
    minCreditsPerCall: role?.minCreditsPerCall ?? 1,
    reserveCap: role?.reserveCap ?? DEFAULT_RESERVE_CAP,
  };
}

const DIFF_FIELDS: Array<keyof RoleDraft> = [
  'enabled', 'providerId', 'modelId', 'fallbackProviderId', 'fallbackModelId',
  'burnRate', 'minCreditsPerCall', 'reserveCap',
];

export function roleDiff(
  original: RoleDraft,
  draft: RoleDraft,
): Array<{ field: keyof RoleDraft; before: unknown; after: unknown }> {
  return DIFF_FIELDS
    .filter((f) => original[f] !== draft[f])
    .map((f) => ({ field: f, before: original[f], after: draft[f] }));
}

export function validateRoleDraft(
  draft: RoleDraft,
  models: AiModelDto[],
  providers: AiProviderDto[],
): string[] {
  const errors: string[] = [];

  if (draft.enabled) {
    if (!draft.providerId) errors.push('Choose a provider.');
    if (!draft.modelId) errors.push('Choose a model.');
  }

  const find = (p: string, m: string) =>
    models.find((x) => x.providerId === p && x.modelId === m);

  if (draft.providerId && draft.modelId) {
    const primary = find(draft.providerId, draft.modelId);
    if (!primary) {
      errors.push(`Model "${draft.modelId}" does not belong to provider "${draft.providerId}".`);
    } else if (!isModelPriced(primary)) {
      // Spec §3.2 / M8: the router hard-fails on an unpriced model rather than
      // downgrading, so saving one here would take the role offline at runtime.
      errors.push(`Model "${primary.label}" has no price. An unpriced call is an unmetered call — set its unit prices on Providers & Models first.`);
    }
    const provider = providers.find((p) => p.providerId === draft.providerId);
    if (provider && !provider.enabled) {
      errors.push(`Provider "${provider.label}" is disabled — this role will fall through to its fallback.`);
    }
  }

  if (draft.fallbackProviderId && draft.fallbackModelId) {
    if (
      draft.fallbackProviderId === draft.providerId &&
      draft.fallbackModelId === draft.modelId
    ) {
      errors.push('The fallback is the same as the primary. A fallback that matches the primary is not a fallback.');
    }
    const fb = find(draft.fallbackProviderId, draft.fallbackModelId);
    if (!fb) {
      errors.push(`Fallback model "${draft.fallbackModelId}" does not belong to provider "${draft.fallbackProviderId}".`);
    } else if (!isModelPriced(fb)) {
      errors.push(`Fallback model "${fb.label}" has no price. The router refuses an unpriced fallback.`);
    }
  }

  if (!Number.isFinite(draft.burnRate) || draft.burnRate < 0 || draft.burnRate > 50) {
    errors.push('Burn rate must be between 0 and 50.');
  }
  if (!Number.isInteger(draft.minCreditsPerCall)) {
    errors.push('Minimum credits per call must be a whole number.');
  } else if (draft.minCreditsPerCall < 0) {
    errors.push('Minimum credits per call cannot be negative.');
  }
  if (!Number.isInteger(draft.reserveCap) || draft.reserveCap <= 0) {
    errors.push('Reserve cap must be a positive whole number.');
  } else if (draft.reserveCap < draft.minCreditsPerCall) {
    errors.push('Reserve cap is below the minimum charge — the reserve could never cover a single call.');
  }

  return errors;
}

export function buildRolePatch(draft: RoleDraft): Record<string, unknown> {
  const patch: Record<string, unknown> = {
    label: draft.label,
    enabled: draft.enabled,
    providerId: draft.providerId,
    modelId: draft.modelId,
    burnRate: Number(draft.burnRate),
    minCreditsPerCall: Number(draft.minCreditsPerCall),
    reserveCap: Number(draft.reserveCap),
  };
  // Omitted, not empty-stringed: an empty string would be stored as a
  // fallback pointing at nothing, and the router would try to resolve it.
  if (draft.fallbackProviderId && draft.fallbackModelId) {
    patch.fallbackProviderId = draft.fallbackProviderId;
    patch.fallbackModelId = draft.fallbackModelId;
  }
  return patch;
}

const priceHint = (m: AiModelDto): string => {
  if (!isModelPriced(m)) return 'No price set — cannot be routed to';
  const p = m.unitPrices;
  if (p.input_per_million_usd || p.output_per_million_usd) {
    return `$${p.input_per_million_usd ?? 0} in / $${p.output_per_million_usd ?? 0} out per M tokens`;
  }
  if (p.per_image_usd) return `$${p.per_image_usd} per image`;
  if (p.per_second_usd) return `$${p.per_second_usd} per second`;
  if (p.per_call_usd) return `$${p.per_call_usd} per call`;
  return 'Priced';
};

export function modelOptions(
  models: AiModelDto[],
  providerId: string,
): Array<{ value: string; label: string; subtitle?: string }> {
  return modelsForProvider(models, providerId).map((m) => ({
    value: m.modelId,
    label: m.enabled ? m.label : `${m.label} (disabled)`,
    subtitle: priceHint(m),
  }));
}

export function providerOptions(
  providers: AiProviderDto[],
): Array<{ value: string; label: string; subtitle?: string }> {
  return providers.map((p) => ({
    value: p.providerId,
    label: p.label,
    subtitle: p.enabled ? p.vendor : `${p.vendor} · disabled`,
  }));
}
